import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The label/value row list used by every request detail card.
 *
 * This markup was copy-pasted 12 times in the consumer detail page alone and
 * across six files, which is how the RFQ detail page ended up with its own
 * airier two-column `<dl>` and visibly different typography from every other
 * request type. Anything rendering request metadata should compose these two
 * components rather than re-deriving the grid.
 *
 * Pair with `<CardTitle className="text-base">` — the default CardTitle size is
 * noticeably larger and is the other half of why the RFQ page looked oversized.
 */
export function DetailRows({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <dl className={cn("divide-y divide-border", className)}>{children}</dl>;
}

export function DetailRow({
  label,
  children,
  className,
  valueClassName,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Overrides on the `<dd>` only.
   *
   * The inline copies this replaces were not uniform: most values were
   * `text-sm text-foreground`, but a few carried only `text-sm` (links and
   * buttons, which bring their own color) and others no class at all (a
   * `StatusBadge` fills the cell). Migrating those onto a row that hardcodes
   * one value style would have been a silent restyle, so the differences
   * ride here instead — `cn` is tailwind-merge, so `text-base` or a color
   * passed in replaces the default rather than fighting it.
   *
   * This is for the VALUE cell. It is not a general escape hatch: a caller
   * reaching for it to change the grid or the label is re-creating the
   * divergence this component exists to remove.
   */
  valueClassName?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,160px)_1fr] gap-3 py-2 first:pt-0 last:pb-0",
        className,
      )}
    >
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={cn("text-sm text-foreground", valueClassName)}>{children}</dd>
    </div>
  );
}
